/**
 * User & Authentication API Endpoints
 *
 * The DELIBERATE numeric-identity island: the users-permissions plugin resolves
 * users by their numeric `id` (not documentId) and returns flat bodies with no
 * `{ data }` envelope. Hence `id: string | number` here (also accepts the string
 * literal 'me') — everything else in the app should use utils/identity.apiId.
 */
import type { AxiosResponse } from "axios";
import api from "../api";
import type { EntityPayload, QueryParams, StrapiListResponse } from "../../types/api";
import type { AppUser } from "../../types/entities";

export const authApi = {
  /** Login. Returns { jwt, user }. */
  login: (identifier: string, password: string): Promise<AxiosResponse> =>
    api.post("/api/auth/local", { identifier, password }),

  /** Get the current user (flat body). */
  getMe: (): Promise<AxiosResponse<AppUser>> => api.get("/api/users/me"),
};

export const userApi = {
  /** users-permissions may return a bare array OR a { data } envelope. */
  getAll: (
    params: QueryParams = {}
  ): Promise<AxiosResponse<StrapiListResponse<AppUser> | AppUser[]>> =>
    api.get("/api/users", { params }),

  /**
   * Create a user (users-permissions POST /api/users, flat body — no {data}
   * wrapper, matching update and the pre-migration POST).
   */
  create: (data: EntityPayload): Promise<AxiosResponse<AppUser>> => api.post("/api/users", data),

  getById: (id: string | number, params: QueryParams = {}): Promise<AxiosResponse<AppUser>> =>
    api.get(`/api/users/${id}`, { params }),

  /** Update a user (flat body, no {data} wrapper). */
  update: (id: string | number, data: EntityPayload): Promise<AxiosResponse<AppUser>> =>
    api.put(`/api/users/${id}`, data),

  delete: (id: string | number): Promise<AxiosResponse> => api.delete(`/api/users/${id}`),
};
